import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Index" },
  { to: "/projects", label: "Portfolio" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "nav-blur" : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
          >
            <span
              className={`relative block w-11 h-11 md:w-12 md:h-12 overflow-hidden rounded-sm transition-colors duration-500 ${
                scrolled ? "bg-[#1A1A1A]" : "bg-transparent"
              }`}
            >
              <img
                src="https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/2iaunrlz_YACH_Construction_Print_HighRes_300DPI.webp"
                alt="Yach Construction — bison emblem logo"
                className="absolute inset-[-18%] w-[136%] h-[136%] object-contain"
              />
            </span>
            <span
              className={`hidden sm:block font-grotesk text-[10px] md:text-[11px] uppercase tracking-[0.28em] transition-colors duration-500 ${
                scrolled ? "text-[#4A4A4A]" : "text-white/80"
              }`}
            >
              Yach Construction
              <span className="hidden md:inline"> / London ON</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-under font-grotesk text-[12px] uppercase tracking-[0.22em] ${
                    isActive ? "text-[#1A1A1A]" : "text-[#4A4A4A] hover:text-[#1A1A1A]"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <Link to="/quote" className="btn-sharp" data-testid="nav-cta">
              <span>Free Quote</span>
              <span aria-hidden>→</span>
            </Link>
          </div>

          <button
            onClick={() => setOpen(true)}
            className="md:hidden w-10 h-10 flex items-center justify-center border border-[#D9D4C7]"
            data-testid="nav-menu-open"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease: [0.65, 0, 0.35, 1] }}
            className="fixed inset-0 z-[60] bg-[#1A1A1A] text-[#FAF9F6] flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
              <span className="flex items-center gap-3">
                <img
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_build-vessel/artifacts/2iaunrlz_YACH_Construction_Print_HighRes_300DPI.webp"
                  alt="Yach Construction"
                  className="w-10 h-10 object-contain"
                />
                <span className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-white/70">
                  Yach Construction
                </span>
              </span>
              <button
                onClick={() => setOpen(false)}
                className="w-10 h-10 flex items-center justify-center border border-white/20"
                data-testid="nav-menu-close"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-5">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.1 + i * 0.06,
                    ease: [0.65, 0, 0.35, 1],
                  }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="font-display text-5xl block"
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="p-6 border-t border-white/10 font-grotesk text-xs uppercase tracking-[0.24em] text-white/60">
              London, ON · +1 519-702-9417
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
